import React from "react";

/**
 * The table question (SPEC 23.2 / 23.3): `columns` [{key, label, type: text|number|date}], at most
 * `max_rows` rows. A value is an array of row objects, each with every column key.
 *
 *   <TableInput id label columns maxRows value onChange disabled />   editable (pop-up, Raise document)
 *   <TableView columns value />                                         read-only (HR values, documents)
 *
 * Helpers: tableDef (the options object of a table question), blankRow, cleanTable (what is sent:
 * empty rows dropped, an empty cell inside a kept row = null, number cells as numbers) and
 * tableProblem (the client-side check; the server repeats it and answers 422).
 */

export const DEFAULT_MAX_ROWS = 10;

export const tableDef = (question) => {
  const options = question && question.options && !Array.isArray(question.options) ? question.options : {};
  return {
    columns: Array.isArray(options.columns) ? options.columns : [],
    maxRows: Number(options.max_rows) || DEFAULT_MAX_ROWS,
  };
};

export const blankRow = (columns) => {
  const row = {};
  (columns || []).forEach((c) => { row[c.key] = ""; });
  return row;
};

const empty = (v) => v === null || v === undefined || String(v).trim() === "";

// SPEC 23.3: empty rows dropped; text as string (<= 255), number numeric, date Y-m-d; the server
// stores an empty cell of a kept row as null (confirmed by the server build, 30 Oct).
export const cleanTable = (columns, rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .filter((row) => (columns || []).some((c) => !empty(row && row[c.key])))
    .map((row) => {
      const out = {};
      columns.forEach((c) => {
        const v = row[c.key];
        if (empty(v)) out[c.key] = null;
        else if (c.type === "number" && !isNaN(Number(v))) out[c.key] = Number(v);
        else out[c.key] = String(v).trim();
      });
      return out;
    });
};

// A message when the table cannot be sent, otherwise null.
export const tableProblem = (question, rows) => {
  const { columns, maxRows } = tableDef(question);
  const kept = cleanTable(columns, rows);
  if (question.required && kept.length === 0) return "Add at least one row";
  if (kept.length > maxRows) return "At most " + maxRows + " rows";
  for (const row of kept) {
    for (const c of columns) {
      const v = row[c.key];
      if (v === null) continue;
      if (c.type === "number" && typeof v !== "number") return c.label + " must be a number";
      if (c.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return c.label + " must be a date";
      if (c.type === "text" && v.length > 255) return c.label + " is too long";
    }
  }
  return null;
};

const INPUT_TYPE = { text: "text", number: "number", date: "date" };

export const TableInput = ({ id, label, columns, maxRows, value, onChange, disabled }) => {
  const rows = Array.isArray(value) && value.length ? value : [blankRow(columns)];
  const setCell = (r, key, v) => onChange(rows.map((row, i) => (i === r ? { ...row, [key]: v } : row)));
  const addRow = () => onChange(rows.concat([blankRow(columns)]));
  const removeRow = (r) => onChange(rows.filter((_, i) => i !== r));
  const limit = Number(maxRows) || DEFAULT_MAX_ROWS;
  return (
    <div className="broadcast-table-input" id={id} aria-label={label} role="group">
      <table className="table table-sm table-bordered mb-1">
        <thead>
          <tr>
            {columns.map((c) => <th key={c.key}>{c.label}</th>)}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {columns.map((c) => (
                <td key={c.key}>
                  <input type={INPUT_TYPE[c.type] || "text"} className="form-control form-control-sm" aria-label={c.label + " row " + (r + 1)}
                    maxLength={c.type === "text" ? 255 : undefined} value={row[c.key] === null || row[c.key] === undefined ? "" : row[c.key]}
                    disabled={disabled} onChange={(e) => setCell(r, c.key, e.target.value)} />
                </td>
              ))}
              <td>
                <button type="button" className="btn btn-sm btn-outline-danger" aria-label={"Remove row " + (r + 1)}
                  disabled={disabled || rows.length <= 1} onClick={() => removeRow(r)}>&times;</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled || rows.length >= limit} onClick={addRow}>Add row</button>
    </div>
  );
};

export const TableView = ({ columns, value }) => {
  const rows = Array.isArray(value) ? value : [];
  if (rows.length === 0) return <span className="text-muted">—</span>;
  return (
    <table className="table table-sm table-bordered broadcast-table-view">
      <thead>
        <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr key={r}>
            {columns.map((c) => <td key={c.key}>{row[c.key] === null || row[c.key] === undefined ? "" : String(row[c.key])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// One canonical value read-only: a table as a table, yes_no Yes / No, multi joined, others as is.
export const ValueView = ({ question, value }) => {
  if (question.qtype === "table") return <TableView columns={tableDef(question).columns} value={value} />;
  if (value === null || value === undefined || value === "") return <span className="text-muted">—</span>;
  if (Array.isArray(value)) return <span>{value.join("; ")}</span>;
  if (question.qtype === "yes_no") return <span>{Number(value) === 1 ? "Yes" : "No"}</span>;
  return <span>{String(value)}</span>;
};
